import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  Calendar, 
  UserCheck, 
  Home, 
  LogOut, 
  DollarSign,
  AlertCircle,
  Sparkles,
  ArrowRight
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FrontDesk = () => {
  const { selectedProperty } = useOutletContext();
  const { t } = useTranslation();
  const [arrivals, setArrivals] = useState([]);
  const [inHouse, setInHouse] = useState([]);
  const [departures, setDepartures] = useState([]);
  const [dirtyRooms, setDirtyRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Check-in modal state
  const [showCheckInModal, setShowCheckInModal] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState('Cash');
  
  // Check-out modal state
  const [showCheckOutModal, setShowCheckOutModal] = useState(false);
  const [checkoutReservation, setCheckoutReservation] = useState(null);

  const today = format(new Date(), 'yyyy-MM-dd');

  useEffect(() => {
    if (selectedProperty) {
      fetchTodayData();
    }
  }, [selectedProperty]);

  const getToken = () => localStorage.getItem('access_token');

  const fetchTodayData = async () => {
    try {
      setLoading(true);
      const token = getToken();
      
      console.log('[FrontDesk] Fetching today data for property:', selectedProperty.id);
      console.log('[FrontDesk] Today date:', today);
      
      // Fetch arrivals (check-in today)
      const arrivalsRes = await axios.get(
        `${API}/reservations?property_id=${selectedProperty.id}&status=CONFIRMED`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const todayArrivals = arrivalsRes.data.filter(r => r.check_in === today);
      console.log('[FrontDesk] Arrivals fetched:', todayArrivals.length);
      setArrivals(todayArrivals);
      
      // Fetch in-house
      const inHouseRes = await axios.get(
        `${API}/reservations?property_id=${selectedProperty.id}&status=IN_HOUSE`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log('[FrontDesk] In-house fetched:', inHouseRes.data.length);
      setInHouse(inHouseRes.data);
      
      // Fetch departures (check-out today)
      const departuresRes = await axios.get(
        `${API}/reservations?property_id=${selectedProperty.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const todayDepartures = departuresRes.data.filter(
        r => r.check_out === today && r.status === 'IN_HOUSE'
      );
      console.log('[FrontDesk] Departures fetched:', todayDepartures.length);
      setDepartures(todayDepartures);
      
      // Fetch dirty/OOO rooms
      const unitsRes = await axios.get(
        `${API}/properties/${selectedProperty.id}/units`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const dirty = unitsRes.data.filter(u => ['DIRTY', 'OOO', 'IN_PROGRESS'].includes(u.status));
      console.log('[FrontDesk] Dirty rooms fetched:', dirty.length);
      setDirtyRooms(dirty);
      
      console.log('[FrontDesk] ✓ All data fetched successfully');
      
    } catch (error) {
      console.error('[FrontDesk] Error fetching today data:', error);
      console.error('[FrontDesk] Error details:', error.response?.data);
      toast.error('Failed to load front desk data');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckIn = async (reservation) => {
    // Check if room is clean
    const token = getToken();
    try {
      const unitRes = await axios.get(
        `${API}/properties/${selectedProperty.id}/units/${reservation.unit_id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (unitRes.data.status !== 'CLEAN') {
        const confirmed = window.confirm(
          `Room ${unitRes.data.name} is ${unitRes.data.status}. Check in anyway?`
        );
        if (!confirmed) return;
      }
      
      setSelectedReservation(reservation);
      setPaymentAmount(reservation.base_amount + reservation.tax_amount);
      setShowCheckInModal(true);
      
    } catch (error) {
      console.error('Error checking room status:', error);
      toast.error('Failed to check room status');
    }
  };

  const confirmCheckIn = async () => {
    try {
      const token = getToken();
      
      // Update reservation status to IN_HOUSE
      await axios.patch(
        `${API}/reservations/${selectedReservation.id}`,
        { 
          status: 'IN_HOUSE',
          check_in_actual: new Date().toISOString()
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Record payment if amount > 0
      if (paymentAmount > 0) {
        await axios.post(
          `${API}/payments`,
          {
            reservation_id: selectedReservation.id,
            amount: paymentAmount,
            payment_method: paymentMethod,
            notes: 'Payment at check-in'
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      
      toast.success('Guest checked in successfully');
      setShowCheckInModal(false);
      setSelectedReservation(null);
      fetchTodayData();
      
    } catch (error) {
      console.error('Error checking in:', error);
      toast.error('Failed to check in guest');
    }
  };

  const handleCheckOut = async (reservation) => {
    setCheckoutReservation(reservation);
    setShowCheckOutModal(true);
  };

  const confirmCheckOut = async () => {
    try {
      const token = getToken();
      
      // Update reservation status to CHECKED_OUT
      await axios.patch(
        `${API}/reservations/${checkoutReservation.id}`,
        { 
          status: 'CHECKED_OUT',
          check_out_actual: new Date().toISOString()
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Set room to DIRTY
      await axios.patch(
        `${API}/units/${checkoutReservation.unit_id}/status`,
        { status: 'DIRTY' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Guest checked out successfully');
      setShowCheckOutModal(false);
      setCheckoutReservation(null);
      fetchTodayData();
      
    } catch (error) {
      console.error('Error checking out:', error);
      toast.error('Failed to check out guest');
    }
  };

  if (!selectedProperty) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-slate-600">{t('common.selectProperty')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent">
          {t('frontDesk.title')}
        </h1>
        <p className="text-slate-600 mt-2">
          {format(new Date(), 'EEEE, MMMM dd, yyyy')}
        </p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">{t('frontDesk.arrivingToday')}</CardTitle>
            <UserCheck className="h-4 w-4 text-cyan-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{arrivals.length}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">{t('frontDesk.inHouse')}</CardTitle>
            <Home className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{inHouse.length}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">{t('frontDesk.departingToday')}</CardTitle>
            <LogOut className="h-4 w-4 text-orange-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{departures.length}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">{t('frontDesk.roomsNeedingAttention')}</CardTitle>
            <AlertCircle className="h-4 w-4 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{dirtyRooms.length}</div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="arrivals" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="arrivals">{t('frontDesk.arrivals')} ({arrivals.length})</TabsTrigger>
          <TabsTrigger value="in-house">{t('frontDesk.inHouse')} ({inHouse.length})</TabsTrigger>
          <TabsTrigger value="departures">{t('frontDesk.departures')} ({departures.length})</TabsTrigger>
        </TabsList>

        {/* Arrivals Tab */}
        <TabsContent value="arrivals" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <UserCheck className="h-5 w-5" />
                {t('frontDesk.arrivingToday')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <p className="text-center py-8 text-slate-600">{t('common.loading')}</p>
              ) : arrivals.length > 0 ? (
                <div className="space-y-3">
                  {arrivals.map((res) => (
                    <div key={res.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-slate-50">
                      <div className="flex-1">
                        <p className="font-medium">{res.guest?.name || 'Guest'}</p>
                        <p className="text-sm text-slate-600">
                          {t('calendar.room')}: {res.unit_id || 'Not assigned'} • {res.adults} adults
                        </p>
                        <p className="text-xs text-slate-500">
                          {res.check_in} to {res.check_out}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => handleCheckIn(res)}>
                          {t('btn.checkIn')}
                        </Button>
                        <Button size="sm" variant="outline">
                          {t('btn.view')}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center py-8 text-slate-600">No arrivals today</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* In-House Tab */}
        <TabsContent value="in-house" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Home className="h-5 w-5" />
                Currently In-House
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <p className="text-center py-8 text-slate-600">Loading...</p>
              ) : inHouse.length > 0 ? (
                <div className="space-y-3">
                  {inHouse.map((res) => (
                    <div key={res.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-slate-50">
                      <div className="flex-1">
                        <p className="font-medium">{res.guest?.name || 'Guest'}</p>
                        <p className="text-sm text-slate-600">
                          Room: {res.unit_id || 'Not assigned'}
                        </p>
                        <p className="text-xs text-slate-500">
                          Check-out: {res.check_out}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline">
                          Add Charge
                        </Button>
                        <Button size="sm" variant="outline">
                          View
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center py-8 text-slate-600">No guests in-house</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Departures Tab */}
        <TabsContent value="departures" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <LogOut className="h-5 w-5" />
                Departing Today
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <p className="text-center py-8 text-slate-600">Loading...</p>
              ) : departures.length > 0 ? (
                <div className="space-y-3">
                  {departures.map((res) => (
                    <div key={res.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-slate-50">
                      <div className="flex-1">
                        <p className="font-medium">{res.guest?.name || 'Guest'}</p>
                        <p className="text-sm text-slate-600">
                          Room: {res.unit_id || 'Not assigned'}
                        </p>
                        <p className="text-xs text-slate-500">
                          Total: ${(res.base_amount + res.tax_amount).toFixed(2)}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => handleCheckOut(res)}>
                          {t('btn.checkOut')}
                        </Button>
                        <Button size="sm" variant="outline">
                          {t('btn.view')}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center py-8 text-slate-600">No departures today</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dirty/OOO Rooms Alert */}
      {dirtyRooms.length > 0 && (
        <Card className="border-orange-200 bg-orange-50">
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2 text-orange-900">
              <Sparkles className="h-4 w-4" />
              Rooms Needing Attention
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {dirtyRooms.map((room) => (
                <Badge key={room.id} variant="outline" className="text-orange-700">
                  {room.name} - {room.status}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Check-In Modal */}
      <Dialog open={showCheckInModal} onOpenChange={setShowCheckInModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Check In Guest</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-cyan-50 border border-cyan-200 rounded-lg">
              <p className="font-medium text-cyan-900">{selectedReservation?.guest?.name}</p>
              <p className="text-sm text-cyan-700">
                Room: {selectedReservation?.unit_id || 'Not assigned'}
              </p>
              <p className="text-sm text-cyan-700">
                {selectedReservation?.check_in} to {selectedReservation?.check_out}
              </p>
            </div>

            <div>
              <Label htmlFor="payment-amount">Payment Amount ($)</Label>
              <Input
                id="payment-amount"
                type="number"
                step="0.01"
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(Number(e.target.value))}
              />
              <p className="text-xs text-slate-500 mt-1">
                Total due: ${selectedReservation && (selectedReservation.base_amount + selectedReservation.tax_amount).toFixed(2)}
              </p>
            </div>

            <div>
              <Label htmlFor="payment-method">Payment Method</Label>
              <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                <SelectTrigger id="payment-method">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Cash">Cash</SelectItem>
                  <SelectItem value="Credit Card">Credit Card</SelectItem>
                  <SelectItem value="Direct Billing">Direct Billing</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex gap-2">
              <Button onClick={confirmCheckIn} className="flex-1">
                Confirm Check-In
              </Button>
              <Button variant="outline" onClick={() => setShowCheckInModal(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Check-Out Modal */}
      <Dialog open={showCheckOutModal} onOpenChange={setShowCheckOutModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Check Out Guest</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg">
              <p className="font-medium">{checkoutReservation?.guest?.name}</p>
              <p className="text-sm text-slate-600">
                Room: {checkoutReservation?.unit_id || 'Not assigned'}
              </p>
              <p className="text-sm text-slate-600">
                {checkoutReservation?.check_in} to {checkoutReservation?.check_out}
              </p>
            </div>

            <div className="p-4 border border-slate-200 rounded-lg">
              <h3 className="font-medium mb-3">Folio Summary</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-600">Room Charges:</span>
                  <span className="font-medium">${checkoutReservation?.base_amount.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Tax:</span>
                  <span className="font-medium">${checkoutReservation?.tax_amount.toFixed(2)}</span>
                </div>
                <div className="flex justify-between border-t pt-2">
                  <span className="font-medium">Total:</span>
                  <span className="font-bold text-lg">
                    ${checkoutReservation && (checkoutReservation.base_amount + checkoutReservation.tax_amount).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>

            <div className="p-3 bg-orange-50 border border-orange-200 rounded">
              <p className="text-sm text-orange-800">
                ⚠️ Room will be marked as DIRTY after check-out
              </p>
            </div>

            <div className="flex gap-2">
              <Button onClick={confirmCheckOut} className="flex-1">
                Confirm Check-Out
              </Button>
              <Button variant="outline" onClick={() => setShowCheckOutModal(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FrontDesk;